// @flow
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { forEach, groupBy } from 'lodash';
import cx from 'classnames';

import Checkbox from 'components/Checkbox';
import { DisabledTooltipLayer } from './SelectFromList';
import EventTracking from 'lib/EventTracking';
import MediaObject from 'components/MediaObject';
import OrgAvatar from 'organizations/components/OrgAvatar';
import { selectRecipient } from 'reducers/roleBasedMessagingSearch';
import TargetFilterMenu from 'chat/components/TargetFilterMenu';
import Text from 'components/Text';
import type { UIEventContext } from './ComposerContext';

type Props = {
  recipient: OrganizationRecipient,
  recipientIsSelected: boolean,
  disabledCopy: ?string,
  uiEventContext: UIEventContext,

  // Redux dispatchers
  onSelectRecipient: (recipient: OrganizationRecipient, filter: ?MessageFilterType) => *,
};

class OrganizationRecipientItem extends React.Component<Props, *> {
  componentDidMount() {
    EventTracking.trackKey('announcements.share_to.organization.view', {
      organization_uuid: this.props.recipient.organization.uuid,
      ...this.props.uiEventContext,
    });
  }

  logSelectionEvent = () => {
    const orgUuid = this.props.recipient.organization.uuid;

    if (this.props.recipientIsSelected) {
      EventTracking.trackKey('announcements.share_to.organization.unselect.click', {
        organization_uuid: orgUuid,
        ...this.props.uiEventContext,
      });
      return;
    }

    EventTracking.trackKey('announcements.share_to.organization.select.click', {
      organization_uuid: orgUuid,
      ...this.props.uiEventContext,
    });
  };

  handleTopLevelSelection = () => {
    this.props.onSelectRecipient(this.props.recipient, null);
  };

  render() {
    const { recipient, disabledCopy, recipientIsSelected } = this.props;
    const { organization } = recipient;
    const filterArray = [];
    const groupedFilters = groupBy(recipient.filters, 'type');
    forEach(groupedFilters, (values, type) => {
      filterArray.push({ type, values: values.map(value => value.value) });
    });

    const component = (
      <li
        className={cx('RecipientItem', {
          'RecipientItem--disabled': !!disabledCopy,
        })}
        onClick={() => {
          this.handleTopLevelSelection();
          this.logSelectionEvent();
        }}
        data-test-selector={`split-composer-organization-${organization.id}`}>
        <MediaObject figure="right" center>
          <MediaObject gutter="small" center>
            <OrgAvatar size="medium" />
            <div>
              <Text size="small" element="div" truncate>
                {organization.name}
              </Text>
              {filterArray.length
                ? filterArray.map(filter => (
                    <TargetFilterMenu
                      key={filter.type}
                      filter={filter}
                      recipient={recipient}
                      recipientIsSelected={recipientIsSelected}
                    />
                  ))
                : null}
            </div>
          </MediaObject>
          <Checkbox checked={recipientIsSelected} onChange={this.handleTopLevelSelection} />
        </MediaObject>
      </li>
    );
    return disabledCopy ? (
      <DisabledTooltipLayer text={disabledCopy}>{component}</DisabledTooltipLayer>
    ) : (
      component
    );
  }
}

const mapDispatchToProps = (dispatch: Function) => {
  return bindActionCreators(
    {
      onSelectRecipient: selectRecipient,
    },
    dispatch
  );
};

export { OrganizationRecipientItem };

export default connect(undefined, mapDispatchToProps)(OrganizationRecipientItem);
